import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { normalizeSiteChatConfig } from '../phrases';
import type {
    SiteChatActions,
    SiteChatAttachment,
    SiteChatAuthor,
    SiteChatButton,
    SiteChatConfig,
    SiteChatLang,
    SiteChatMessage,
    SiteChatMode,
    SiteChatModel,
    SiteChatUpload,
    SiteChatView,
    SiteChatVoice,
} from '../types';
import { SITE_CHAT_LANGS } from '../types';
import { siteChatUi } from '../ui';
import { SiteChatApi, SiteChatError, type SessionResponse } from './api';
import { CONTACT_EVENT, pageContactId, validContactId } from './contact';
import { applyPageSettings, mergePageSettings, parsePageSettings, visitorFormValue, type SiteChatPageSettings, type SiteChatVisitor } from './pageSettings';
import { isValidPhone } from '../widget/phone';
import { isFormRequired, isOnlineNow, isShownOn, triggerApplies, triggerKey } from './rules';
import { readDevice } from './device';
import { canCaptureScreen, captureScreen } from './screenshot';
import { csatAnswerable, isCsatScore, withCsatAnswer } from './csat';

type Status = 'loading' | 'ready' | 'hidden' | 'error';

type State = {
    status: Status;
    /** The first session request has answered (or failed): from now on the widget is drawn with the channel's own config. */
    booted: boolean;
    config: SiteChatConfig;
    channelName: string;
    shown: boolean;
    hiddenByTrigger: boolean;
    open: boolean;
    view: SiteChatView;
    online: boolean;
    leadSubmitted: boolean;
    /** The visitor skipped the optional form in this browser: it is asked again only once it becomes required. */
    leadSkipped: boolean;
    /** The server refused a message with `lead_required` (hours or settings changed under the visitor). */
    formForced: boolean;
    formValues: Record<string, string>;
    formErrors: Record<string, string>;
    formState: 'idle' | 'sending' | 'network';
    messages: SiteChatMessage[];
    localMessages: SiteChatMessage[];
    hasMore: boolean;
    typing: SiteChatAuthor | null;
    /** The AI reply being written (`draft` hub event); shown as the thread's last bubble until the reply replaces it. */
    draft: DraftState | null;
    /** The channel's team from the session. */
    team: SiteChatAuthor[];
    /** Who took the conversation: from the session, the message pages and the `operators` hub event. */
    operators: SiteChatAuthor[];
    unread: number;
    /** How far an operator has read (-1: none yet); replies in the thread raise it in getModel. */
    readIndex: number;
    hint: string | null;
    hintKey: string | null;
    text: string;
    uploads: SiteChatUpload[];
    composerError: string | null;
    capturing: boolean;
    hiddenByApi: boolean;
};

type Stored = {
    visitorId?: string | null;
    dismissedTriggers?: string[];
    leadSkipped?: boolean;
    /** Messages waiting for the required form, so a reload does not lose them. Voice recordings are not kept. */
    held?: Array<Pick<SiteChatMessage, 'clientId' | 'text' | 'createdDate' | 'attachments'>>;
};

const FORBIDDEN_EXTENSIONS = ['exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'ps1', 'vbs', 'js', 'jar', 'sh', 'apk', 'dll', 'html', 'htm', 'xhtml', 'svg', 'hta', 'lnk'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type SiteChatControllerOptions = {
    /** The snippet's `data-channel`. Empty in `school` mode: the server picks the school's membership chat. */
    channel: string;
    apiUrl: string;
    /** The page's own window: storage, location and media queries are read from it. */
    win?: Window;
    mode?: SiteChatMode;
    /** `school` mode: the host's platform token (a student's School token or an admin's), read fresh on every session request. */
    getSchoolToken?: () => string | null | undefined;
    /**
     * `school` mode: whose session it is. `school` — a student in the membership area (their in-app support thread);
     * `admin` — a school admin in the admin panel talking to AXL's own support channel.
     */
    session?: 'school' | 'admin';
    /** The embed: `window.AXLChatSettings` as the page set it (checked by `parsePageSettings`). */
    page?: unknown;
};

/**
 * What the page can listen to through the public API (`AXLChat('on', …)`, `handle.on(…)`). `message` is a reply of the
 * school's side; `messageSent` the visitor's own message (`{ text, files, voice }`); `formSubmitted` the lead form
 * accepted by the server (`{ name, email, phone }`).
 */
export type SiteChatEvent = 'ready' | 'open' | 'close' | 'unread' | 'message' | 'messageSent' | 'formSubmitted';

/**
 * The live widget: session, socket, thread, form, files, rules and triggers. React subscribes to it through
 * `subscribe` / `getModel` (useSyncExternalStore), and renders the same `WidgetView` the admin preview does.
 *
 * The socket opens only when the visitor has a conversation or opens the panel: a visitor who never chats
 * costs AccelChat one bootstrap request, not a WebSocket for the whole visit.
 */
export class SiteChatController {
    private state: State;
    private listeners = new Set<() => void>();
    private model: SiteChatModel | null = null;
    private api: SiteChatApi;
    private win: Window;
    private token: string | null = null;
    private tokenExpires = 0;
    private tokenRefresh: Promise<string | null> | null = null;
    private visitorId: string | null = null;
    private clockOffset = 0;
    private maxFileSize = 20 * 1024 * 1024;
    private hub: HubConnection | null = null;
    private hubStarting: Promise<void> | null = null;
    private timers: number[] = [];
    private triggerTimers: number[] = [];
    private typingTimer: number | null = null;
    /** Drops a draft whose reply never came (the worker died, the socket missed the withdrawal). */
    private draftTimer: number | null = null;
    private lastTypingSent = 0;
    private readTimer: number | null = null;
    /** The open thread shows its newest message (the view reports it; a thread opens at its end). */
    private threadAtBottom = true;
    private lastUrl = '';
    /** «Opened the chat» went to the analytics for this page view (a new address of a single-page site is a new one). */
    private openedReported = false;
    private disposed = false;
    /** A click on the button while the session was still starting: the panel opens once it is ready. */
    private openRequested = false;
    /** The page's contact last seen (sent with the session or since): a new one signs the visitor in. */
    private lastContactId: string | null = null;
    /** Contacts already tried: the server answers each one once, a second try would change nothing. */
    private triedContacts = new Set<string>();
    /**
     * The inbox debounces its typing call and fires once more AFTER the operator sends, and the bus may deliver
     * that after the message itself. Typing that lands right after a reply is that echo, not new typing.
     */
    private typingMutedUntil = 0;
    private readonly mode: SiteChatMode;
    /** `AXLChat('setData', …)`: what the site knows about the visitor, sent with the lead form and every message. */
    private siteData: Record<string, string> = {};
    private eventListeners = new Map<SiteChatEvent, Set<(payload?: unknown) => void>>();
    private lastEmitted = { status: '', open: false, unread: 0 };
    /** The channel's config as the server gave it; `state.config` is it with this page's settings applied. */
    private channelConfig: SiteChatConfig;
    /** `window.AXLChatSettings` + `AXLChat('configure', …)`: this page's own changes, never saved. */
    private page: SiteChatPageSettings = {};
    /** `AXLChat('setVisitor', …)`: filled into the lead form once the channel's fields are known. */
    private visitor: SiteChatVisitor = {};
    /** Form values the page put in (by field index): a later `setVisitor` may replace them, the visitor's typing it may not. */
    private prefilled: Record<string, string> = {};
    /** `AXLChat('trigger', …)` called before the channel's triggers were known: it runs once they are. */
    private pendingTrigger: string | null = null;
    /** `AXLChatSettings.identity` / `AXLChat('identify', …)`: the site backend's signed visitor token, sent with the session. */
    private identity: string | null = null;
    /** `AXLChat('setContact', …)`: contact fields by key, sent with the form and every message; the server keeps the allowed ones. */
    private contactFields: Record<string, string> = {};

    constructor(private options: SiteChatControllerOptions) {
        this.mode = options.mode ?? 'site';
        this.win = options.win ?? window;
        this.api = new SiteChatApi(options.apiUrl.replace(/\/+$/, ''), () => this.getToken());
        const lang = browserLang(this.win);
        this.state = {
            status: 'loading',
            booted: false,
            config: normalizeSiteChatConfig(null, lang),
            channelName: '',
            shown: true,
            hiddenByTrigger: false,
            open: false,
            view: options.mode === 'school' ? 'thread' : 'home',
            online: true,
            leadSubmitted: options.mode === 'school',
            leadSkipped: false,
            formForced: false,
            formValues: {},
            formErrors: {},
            formState: 'idle',
            messages: [],
            localMessages: [],
            hasMore: false,
            typing: null,
            draft: null,
            team: [],
            operators: [],
            unread: 0,
            readIndex: -1,
            hint: null,
            hintKey: null,
            text: '',
            uploads: [],
            composerError: null,
            capturing: false,
            hiddenByApi: false,
        };
        this.channelConfig = this.state.config;
        this.configure(options.page);
    }

    // ---------- store plumbing ----------

    subscribe = (listener: () => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    get status() {
        return this.state.status;
    }

    get visible() {
        return this.state.status !== 'hidden' && this.state.shown && !this.state.hiddenByTrigger && !this.state.hiddenByApi;
    }

    // ---------- public API (AXLChat, mount handles) ----------

    /** `AXLChat('hide')`: no button and no panel until `show`; the conversation and the socket stay. */
    hide() {
        this.set({ hiddenByApi: true, open: false });
    }

    show() {
        this.set({ hiddenByApi: false });
    }

    /** `AXLChat('showHint', text)`: the hint bubble beside the button now, as a trigger's phrase shows it. */
    showHint(text: string) {
        const value = String(text ?? '').trim().slice(0, 500);
        if (value && !this.isSchool && !this.state.open)
            this.set({ hint: value, hintKey: null });
    }

    /**
     * `AXLChat('trigger', name)`: the channel's trigger of that name runs now. Its delay and page conditions are the
     * page's call, and `triggers: false` does not stop it; it must still be on, and «during / outside working hours»
     * still holds. Called before the session answers, it runs once the channel's triggers are known.
     */
    trigger(name: string): boolean {
        const wanted = String(name ?? '').trim().toLowerCase();
        if (!wanted || this.isSchool)
            return false;
        if (this.state.status !== 'ready') {
            this.pendingTrigger = wanted;
            return false;
        }
        const trigger = (this.channelConfig.triggers ?? []).find(item => item.name?.trim().toLowerCase() === wanted);
        const online = isOnlineNow(this.state.config, this.now());
        if (!trigger?.enabled || trigger.when === 'work' && !online || trigger.when === 'off' && online)
            return false;
        if (trigger.action === 'hide') {
            this.set({ hiddenByTrigger: true, open: false });
            return true;
        }
        if (this.state.open || !trigger.text?.trim())
            return false;
        this.set({ hint: trigger.text.trim(), hintKey: triggerKey(trigger) });
        return true;
    }

    /** Merged over earlier calls; an empty value removes the key. Reaches the operator with the next message or form. */
    setData(values: Record<string, unknown>) {
        for (const [key, value] of Object.entries(values ?? {})) {
            const name = String(key).trim();
            if (!name)
                continue;
            if (value == null || String(value).trim() === '')
                delete this.siteData[name];
            else
                this.siteData[name] = String(value);
        }
    }

    /**
     * `AXLChat('configure', …)` and `window.AXLChatSettings`: this page's changes over the channel's settings
     * (`runtime/pageSettings.ts`); `null` gives a key back to the channel. `visitor` and `data` go to `setVisitor` /
     * `setData`.
     */
    configure(raw: unknown) {
        const { patch, visitor, data, contact, identity } = parsePageSettings(raw);
        if (data)
            this.setData(data);
        if (visitor)
            this.setVisitor(visitor);
        if (contact)
            this.setContact(contact);
        if (identity !== undefined)
            this.identify(identity);
        if (!Object.keys(patch).length)
            return;
        this.page = mergePageSettings(this.page, patch);
        this.set({
            config: applyPageSettings(this.channelConfig, this.page),
            // Triggers switched off on a live page: what they already did goes too.
            ...(this.page.triggers === false ? { hint: null, hintKey: null, hiddenByTrigger: false } : {}),
        });
        if (this.state.status === 'ready' && !this.isSchool && ('rules' in patch || 'triggers' in patch)) {
            this.evaluateDisplay();
            this.scheduleTriggers();
        }
    }

    /**
     * `AXLChat('setContact', …)`: contact fields written straight to the visitor's contact, without the form — only the
     * keys the channel allows, only where the contact has nothing (the server decides; `SiteChatPageFields`). An empty
     * value takes a key back.
     */
    setContact(values: Record<string, string>) {
        for (const [key, value] of Object.entries(values))
            if (value === '')
                delete this.contactFields[key];
            else
                this.contactFields[key] = value;
    }

    /**
     * `AXLChat('identify', token)`: a visitor token the site's backend signed with the channel's key. The session is
     * asked again with it — the visitor becomes that contact, with its earlier conversation and no form. `null` forgets it
     * for the next session.
     */
    identify(token: string | null) {
        if (token === this.identity)
            return;
        this.identity = token;
        if (token && !this.isSchool && this.state.status !== 'loading')
            void this.start();
    }

    /** The page's settings in force (the embed reads `zIndex` off them). */
    get pageSettings(): SiteChatPageSettings {
        return this.page;
    }

    /**
     * `AXLChat('setVisitor', …)`: name, email, phone and contact-field keys into the lead form fields bound to them; the
     * visitor still sends it.
     */
    setVisitor(visitor: SiteChatVisitor) {
        this.visitor = { ...this.visitor, ...visitor };
        // Field indexes are the channel's: before the session the defaults' order may not be its order.
        if (this.state.status === 'ready')
            this.prefillForm();
    }

    private prefillForm() {
        const values = { ...this.state.formValues };
        let changed = false;
        this.state.config.fields.forEach((field, index) => {
            const raw = visitorValueFor(field, this.visitor);
            const value = raw === undefined ? null : visitorFormValue(field, raw);
            const current = values[index] ?? '';
            if (value == null || value === current || (current && current !== this.prefilled[index]))
                return;
            values[index] = value;
            this.prefilled[index] = value;
            changed = true;
        });
        if (changed)
            this.set({ formValues: values });
    }

    /** Puts text in the composer; the visitor still sends it. Combine with `open` to show it. */
    setText(text: string) {
        this.actions.setText(String(text ?? ''));
    }

    /** An SPA can report navigation at once instead of waiting for the next second's URL check. */
    pageView() {
        if (!this.isSchool && this.state.status === 'ready')
            this.tick();
    }

    on(event: SiteChatEvent, listener: (payload?: unknown) => void) {
        let set = this.eventListeners.get(event);
        if (!set)
            this.eventListeners.set(event, set = new Set());
        set.add(listener);
        return () => this.off(event, listener);
    }

    off(event: SiteChatEvent, listener: (payload?: unknown) => void) {
        this.eventListeners.get(event)?.delete(listener);
    }

    private emit(event: SiteChatEvent, payload?: unknown) {
        this.eventListeners.get(event)?.forEach(listener => {
            try {
                listener(payload);
            } catch (e) {
                // A page's handler must not break the widget.
                console.error('[AXLChat]', e);
            }
        });
    }

    /** Turns state changes into the page-facing events. */
    private emitChanges() {
        const s = this.state;
        const last = this.lastEmitted;
        if (s.status !== last.status && s.status === 'ready')
            this.emit('ready');
        if (s.open !== last.open)
            this.emit(s.open ? 'open' : 'close');
        if (s.unread !== last.unread)
            this.emit('unread', s.unread);
        this.lastEmitted = { status: s.status, open: s.open, unread: s.unread };
    }

    get isSchool() {
        return this.mode === 'school';
    }

    get isOpen() {
        return this.state.open;
    }

    get unread() {
        return this.state.unread;
    }

    open() {
        if (!this.state.open)
            this.actions.toggle();
    }

    close() {
        this.actions.close();
    }

    toggle() {
        this.actions.toggle();
    }

    getModel = (): SiteChatModel => {
        if (this.model)
            return this.model;
        const s = this.state;
        this.model = {
            config: s.config,
            mode: this.mode,
            launcher: this.page.launcher !== false,
            channelName: s.channelName,
            online: s.online,
            open: s.open,
            view: s.view,
            loading: s.status === 'loading',
            booted: s.booted,
            connectionError: s.open && s.status === 'error',
            unread: s.unread,
            hint: s.hint,
            messages: [...s.localMessages, ...s.messages, ...draftRow(s.draft, s.messages)],
            // the bubble being written says more than the dots
            typing: s.draft ? null : s.typing,
            team: s.team,
            operators: s.operators,
            hasMore: s.hasMore,
            readIndex: readIndexOf(s.messages, s.readIndex),
            canScreenshot: canCaptureScreen(this.win, isPhone(this.win)),
            capturing: s.capturing,
            form: { ...this.formStatus(), values: s.formValues, errors: s.formErrors, state: s.formState, submitted: s.leadSubmitted },
            composer: { text: s.text, uploads: s.uploads, error: s.composerError },
            actions: this.actions,
        };
        return this.model;
    };

    /** Whether this visitor is asked for the lead form at all, and whether it is a must right now. */
    private formStatus(): { shown: boolean; required: boolean } {
        const s = this.state;
        const enabled = !this.isSchool && s.config.formEnabled !== false && s.config.fields.some(field => field.enabled) && !s.leadSubmitted;
        const required = enabled && (s.formForced || isFormRequired(s.config, s.online));
        // Under the visitor's first message: a visitor who has not written yet is not asked anything.
        const wrote = s.messages.some(message => message.fromVisitor && !message.local);
        return { shown: enabled && wrote && (required || !s.leadSkipped), required };
    }

    private set(patch: Partial<State>) {
        const wasOpen = this.state.open;
        this.state = { ...this.state, ...patch };
        this.model = null;
        this.listeners.forEach(listener => listener());
        if (this.eventListeners.size > 0)
            this.emitChanges();
        // Every way of opening (button, hint, SDK, a click while loading) ends here.
        if (!wasOpen && this.state.open)
            this.reportOpened();
    }

    /**
     * The website chat analytics' «opened the chat»: once per page view, on a website only (not the School area or the
     * admin's support chat), with a session. Fire and forget — the server always answers 200 and limits the rate.
     */
    private reportOpened() {
        if (this.mode !== 'site' || this.openedReported || !this.token)
            return;
        this.openedReported = true;
        void this.api.opened(this.win.location.href).catch(() => undefined);
    }

    // ---------- lifecycle ----------

    async start() {
        this.set({ status: 'loading' });
        const stored = this.read();
        // A visitor the page already knows (an earlier form, a link with `a_c`) is signed in by the session itself.
        const contactId = this.isSchool ? null : pageContactId(this.win);
        this.lastContactId = contactId;
        if (contactId)
            this.triedContacts.add(contactId);
        // The link of the «you have a reply» email: that visitor's conversation, on this browser too.
        const resume = this.isSchool ? null : takeResumeToken(this.win);
        let session: SessionResponse;
        try {
            session = await this.requestSession(this.visitorId ?? stored.visitorId ?? null, contactId, resume);
        } catch (e) {
            if (e instanceof SiteChatError && (e.status === 401 || e.status === 403 || e.status === 404)) {
                // A site outside the channel's list, a deleted or disabled channel: no widget at all.
                this.set({ status: 'hidden', booted: true });
                return;
            }
            // Unreachable service: the button appears (with the defaults — nothing better is known) and opens on «no connection».
            this.set({ status: 'error', booted: true });
            return;
        }
        if (this.disposed)
            return;

        this.applySession(session);
        // For the site's developer: a refused token leaves the visitor anonymous, and only the console says why.
        if (session.identityError)
            console.warn(`[AXLChat] identity token refused: ${session.identityError}`);
        if (!this.isSchool)
            this.restoreHeld(stored);
        if (this.openRequested) {
            this.openRequested = false;
            this.actions.toggle();
        }

        if (this.isSchool) {
            // One thread, always reachable: no display rules, no triggers, and the socket is up from the start,
            // so the header badge counts replies while the panel is closed.
            this.clearTimers();
            this.timers.push(this.win.setInterval(() => this.tickSchedule(), 1000));
            this.win.document.addEventListener('visibilitychange', this.onVisibility);
            void this.connectHub();
            this.set({ booted: true });
            return;
        }

        this.write({ visitorId: session.visitorId });
        this.lastUrl = this.win.location.href;
        this.evaluateDisplay();
        // Only now, with the config applied and the display rules evaluated in the same tick, is anything drawn.
        this.set({ booted: true });
        if ((session.resumed || (this.page.openOnLoad && this.visible)) && !this.state.open)
            this.actions.toggle();
        this.scheduleTriggers();
        if (this.pendingTrigger) {
            const name = this.pendingTrigger;
            this.pendingTrigger = null;
            this.trigger(name);
        }
        this.clearTimers();
        this.timers.push(this.win.setInterval(() => this.tick(), 1000));
        this.win.addEventListener(CONTACT_EVENT, this.onContact);
        this.win.document.addEventListener('visibilitychange', this.onVisibility);

        if (this.state.messages.length > 0)
            void this.connectHub();
    }

    dispose() {
        this.disposed = true;
        this.win.removeEventListener(CONTACT_EVENT, this.onContact);
        this.win.document.removeEventListener('visibilitychange', this.onVisibility);
        this.clearTimers();
        this.triggerTimers.forEach(timer => this.win.clearTimeout(timer));
        void this.hub?.stop();
    }

    private applySession(session: SessionResponse) {
        this.token = session.token;
        this.tokenExpires = Date.parse(session.expiresAt) || Date.now() + 60 * 60 * 1000;
        this.visitorId = session.visitorId;
        this.clockOffset = (Date.parse(session.serverTime) || Date.now()) - Date.now();
        this.maxFileSize = session.maxFileSize || this.maxFileSize;

        this.channelConfig = normalizeSiteChatConfig(session.widget, browserLang(this.win));
        const config = applyPageSettings(this.channelConfig, this.page);
        const online = isOnlineNow(config, this.now());
        this.set({
            status: 'ready',
            config,
            channelName: session.channelName ?? '',
            online,
            leadSubmitted: !!session.leadSubmitted,
            leadSkipped: !this.isSchool && !!this.read().leadSkipped,
            formForced: false,
            formValues: { ...(session.lead ?? {}), ...this.state.formValues },
            messages: mergeMessages([], session.messages ?? []),
            hasMore: !!session.hasMore,
            unread: session.unread ?? 0,
            readIndex: session.readIndex ?? -1,
            team: session.team ?? [],
            operators: session.operators ?? [],
        });
        this.prefillForm();
    }

    /** Messages written before a reload while the form was required: back in the thread, or sent if it is filled in now. */
    private restoreHeld(stored: Stored) {
        const held = (stored.held ?? []).filter(item => item.clientId && (item.text?.trim() || item.attachments?.length));
        if (!held.length)
            return;
        const known = new Set(this.state.messages.map(message => message.clientId).filter(Boolean));
        const messages: SiteChatMessage[] = held
            .filter(item => !known.has(item.clientId))
            .map(item => ({
                ...item,
                id: `local-${item.clientId}`,
                index: Number.MAX_SAFE_INTEGER,
                fromVisitor: true,
                createdDate: item.createdDate || this.now().toISOString(),
                status: 'held' as const,
            }));
        this.set({ messages: [...this.state.messages, ...messages] });
        if (this.formStatus().required)
            this.saveHeld();
        else
            void this.releaseHeld();
    }

    private saveHeld() {
        const held = this.state.messages
            .filter(message => message.status === 'held' && !this.voiceFiles.has(message.clientId ?? ''))
            .map(({ clientId, text, createdDate, attachments }) => ({ clientId, text, createdDate, attachments }));
        this.write({ held });
    }

    /** The form is in (or no longer required): what waited for it goes out, in the order it was written. */
    private async releaseHeld() {
        const held = this.state.messages.filter(message => message.status === 'held');
        if (!held.length)
            return;
        this.set({ messages: this.state.messages.map(message => message.status === 'held' ? { ...message, status: 'sending' } : message) });
        this.write({ held: [] });
        for (const message of held)
            await this.deliver({ ...message, status: 'sending' });
    }

    private now() {
        return new Date(Date.now() + this.clockOffset);
    }

    private clearTimers() {
        this.timers.forEach(timer => this.win.clearInterval(timer));
        this.timers = [];
    }

    /** A visitor session (anonymous, keyed by the id this browser keeps) or the signed-in student's one. */
    private requestSession(visitorId: string | null, contactId: string | null = null, resume: string | null = null): Promise<SessionResponse> {
        if (this.isSchool) {
            const token = this.options.getSchoolToken?.();
            if (!token)
                return Promise.reject(new SiteChatError('unauthorized', 401));
            return this.options.session === 'admin'
                ? this.api.startAdminSession(token, { url: this.win.location.href, lang: this.win.navigator.language })
                : this.api.startSchoolSession(token);
        }
        return this.api.startSession({
            channel: this.options.channel,
            visitorId,
            url: this.win.location.href,
            referrer: this.win.document.referrer,
            lang: this.win.navigator.language,
            contactId,
            resume,
            identity: this.identity,
        });
    }

    private tickSchedule() {
        const online = isOnlineNow(this.state.config, this.now());
        if (online !== this.state.online)
            this.set({ online });
    }

    /**
     * Once a second: schedule changes (online ↔ offline), SPA navigation (display rules, triggers), and a contact the
     * page signed in without the event (a pixel older than the event).
     */
    private tick() {
        this.tickSchedule();

        const contactId = pageContactId(this.win);
        if (contactId && contactId !== this.lastContactId) {
            this.lastContactId = contactId;
            void this.signIn(contactId);
        }

        const url = this.win.location.href;
        if (url !== this.lastUrl) {
            this.lastUrl = url;
            this.openedReported = false;
            this.evaluateDisplay();
            this.scheduleTriggers();
        }
    }

    /** The page's sign-in event (see `contact.ts`): the chat signs in at the same moment. */
    private onContact = (event: Event) => {
        const contactId = validContactId((event as CustomEvent<{ contactId?: string } | null>).detail?.contactId);
        if (!contactId)
            return;
        this.lastContactId = contactId;
        void this.signIn(contactId);
    };

    /**
     * Signs a visitor who is nobody yet in as the page's contact. The server decides what it means (`SiteChatService
     * .SignInByContactAsync`): nothing for a visitor who filled in the form or already is a contact, the contact's earlier
     * dialog (another visitor id: the widget switches to it), or this visitor put on the contact. Messages waiting for
     * the form go out, now that no form is asked.
     */
    private async signIn(contactId: string) {
        const s = this.state;
        if (this.isSchool || s.leadSubmitted || s.status !== 'ready' || this.triedContacts.has(contactId))
            return;
        this.triedContacts.add(contactId);

        let session: SessionResponse;
        try {
            session = await this.api.contact(contactId);
        } catch {
            return;
        }
        if (this.disposed || !session?.token)
            return;

        const switched = session.visitorId !== this.visitorId;
        // Written here and not on the server yet: kept across the new session.
        const pending = this.state.messages.filter(message => message.status === 'held' || message.status === 'sending' || message.status === 'failed');
        this.applySession(session);
        this.write({ visitorId: session.visitorId });
        if (pending.length) {
            const known = new Set(this.state.messages.map(message => message.clientId).filter(Boolean));
            this.set({ messages: [...this.state.messages, ...pending.filter(message => !known.has(message.clientId))] });
        }
        if (switched)
            await this.hub?.stop();
        if (this.state.messages.length > 0)
            void this.connectHub();
        if (!this.formStatus().required)
            void this.releaseHeld();
    }

    private evaluateDisplay() {
        const shown = isShownOn(this.state.config, this.win.location.href, isPhone(this.win));
        // A visitor already in a conversation keeps the widget: hiding it would cut them off from the reply.
        this.set({ shown: shown || this.state.messages.length > 0 });
    }

    private scheduleTriggers() {
        this.triggerTimers.forEach(timer => this.win.clearTimeout(timer));
        this.triggerTimers = [];
        const dismissed = new Set(this.read().dismissedTriggers ?? []);
        const url = this.win.location.href;
        for (const trigger of this.state.config.triggers ?? []) {
            const key = triggerKey(trigger);
            if (dismissed.has(key) || !triggerApplies(trigger, url, this.state.online))
                continue;
            const timer = this.win.setTimeout(() => {
                const s = this.state;
                if (s.open || s.messages.length > 0 || !triggerApplies(trigger, this.win.location.href, isOnlineNow(s.config, this.now())))
                    return;
                if (trigger.action === 'hide')
                    this.set({ hiddenByTrigger: true });
                else if (!s.hint)
                    this.set({ hint: trigger.text.trim(), hintKey: key });
            }, Math.max(0, Number(trigger.seconds) || 0) * 1000);
            this.triggerTimers.push(timer);
        }
    }

    // ---------- token and socket ----------

    private async getToken(): Promise<string | null> {
        if (this.token && this.tokenExpires - Date.now() > 2 * 60 * 1000)
            return this.token;
        if (!this.tokenRefresh) {
            this.tokenRefresh = this.requestSession(this.visitorId)
                .then(session => {
                    this.token = session.token;
                    this.tokenExpires = Date.parse(session.expiresAt) || Date.now() + 60 * 60 * 1000;
                    return this.token;
                })
                .catch(() => this.token)
                .finally(() => {
                    this.tokenRefresh = null;
                });
        }
        return this.tokenRefresh;
    }

    private connectHub(): Promise<void> {
        if (this.hub && this.hub.state !== HubConnectionState.Disconnected)
            return this.hubStarting ?? Promise.resolve();
        if (this.hubStarting)
            return this.hubStarting;

        const hub = this.hub ?? new HubConnectionBuilder()
            .withUrl(this.api.hubUrl, {
                accessTokenFactory: async () => (await this.getToken()) ?? '',
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(LogLevel.None)
            .build();

        if (!this.hub) {
            this.hub = hub;
            hub.on('message', (payload: { message?: SiteChatMessage }) => payload?.message && this.receive(payload.message));
            hub.on('messageUpdated', (payload: { message?: SiteChatMessage }) => {
                if (payload?.message)
                    this.set({ messages: mergeMessages(this.state.messages, [payload.message]) });
            });
            hub.on('messageDeleted', (payload: { messageId?: string }) => {
                if (payload?.messageId)
                    this.set({ messages: this.state.messages.filter(message => message.id !== payload.messageId) });
            });
            hub.on('read', (payload: { readIndex?: number | null }) => {
                const index = Number(payload?.readIndex);
                if (Number.isFinite(index) && index > this.state.readIndex)
                    this.set({ readIndex: index });
            });
            hub.on('operators', (payload: { operators?: SiteChatAuthor[] | null }) => {
                if (Array.isArray(payload?.operators))
                    this.set({ operators: payload.operators });
            });
            hub.on('draft', (payload: { draft?: DraftEvent | null }) => payload?.draft && this.onDraft(payload.draft));
            hub.on('typing', (payload: { author?: SiteChatAuthor | null }) => {
                if (Date.now() < this.typingMutedUntil)
                    return;
                this.set({ typing: payload?.author ?? {} });
                if (this.typingTimer)
                    this.win.clearTimeout(this.typingTimer);
                this.typingTimer = this.win.setTimeout(() => this.set({ typing: null }), 6000);
            });
            hub.onreconnected(() => void this.catchUp());
            hub.onclose(() => {
                if (!this.disposed)
                    this.win.setTimeout(() => void this.connectHub(), 15000);
            });
        }

        this.hubStarting = hub.start()
            .then(() => this.catchUp())
            .catch(() => {
                if (!this.disposed)
                    this.win.setTimeout(() => void this.connectHub(), 15000);
            })
            .finally(() => {
                this.hubStarting = null;
            });
        return this.hubStarting;
    }

    /** After a (re)connect: whatever arrived while the socket was down. */
    private async catchUp() {
        try {
            const page = await this.api.messages();
            const before = new Set(this.state.messages.map(message => message.id));
            const fresh = page.messages.filter(message => !before.has(message.id) && !message.fromVisitor);
            this.set({
                messages: mergeMessages(this.state.messages, page.messages),
                readIndex: Math.max(this.state.readIndex, page.readIndex ?? -1),
                ...(Array.isArray(page.operators) ? { operators: page.operators } : {}),
            });
            if (fresh.length)
                this.noteIncoming(fresh.length);
        } catch {
            // The next reconnect tries again.
        }
    }

    /**
     * The AI reply being written: the whole text so far each time (a fallback model may start over), or `withdrawn`
     * when no reply will come. A draft whose reply is already here is late and ignored.
     */
    private onDraft(draft: DraftEvent) {
        if (!draft.id)
            return;
        const current = this.state.draft;
        if (draft.withdrawn) {
            if (current?.id === draft.id)
                this.set({ draft: null });
            return;
        }
        if (!draft.text || this.state.messages.some(message => message.clientId === draft.id))
            return;
        this.set({
            draft: {
                id: draft.id,
                text: draft.text,
                markdown: !!draft.markdown,
                author: draft.author ?? current?.author ?? null,
                createdDate: current?.id === draft.id ? current.createdDate : new Date().toISOString(),
            },
            typing: null,
        });
        if (this.draftTimer)
            this.win.clearTimeout(this.draftTimer);
        this.draftTimer = this.win.setTimeout(() => {
            if (this.state.draft?.id === draft.id)
                this.set({ draft: null });
        }, DRAFT_TIMEOUT_MS);
    }

    private receive(message: SiteChatMessage) {
        const known = this.state.messages.some(item => item.id === message.id);
        if (!message.fromVisitor)
            this.typingMutedUntil = Date.now() + 4000;
        this.set({
            messages: mergeMessages(this.state.messages, [message]),
            typing: message.fromVisitor ? this.state.typing : null,
            draft: message.clientId && message.clientId === this.state.draft?.id ? null : this.state.draft,
        });
        if (!known && !message.fromVisitor) {
            this.noteIncoming(1);
            // The page's `message` listeners get replies; a hand-over note has no text to give them.
            if (!message.notice)
                this.emit('message', { id: message.id, text: message.text ?? '', author: message.author?.name ?? null, createdDate: message.createdDate });
        }
    }

    /**
     * Replies the visitor sees now are read; the rest count as unread — on the launcher badge while the panel is closed,
     * on the thread's jump button while it is scrolled up or the tab is in the background.
     */
    private noteIncoming(count: number) {
        if (this.seesNewest())
            this.scheduleRead();
        else
            this.set({ unread: this.state.unread + count });
    }

    private seesNewest() {
        return this.state.open && this.state.view === 'thread' && this.threadAtBottom && this.win.document.visibilityState !== 'hidden';
    }

    /** Back to a tab where the open thread waited at its end: what arrived meanwhile is read now. */
    private onVisibility = () => {
        if (this.state.unread && this.seesNewest())
            this.scheduleRead();
    };

    private scheduleRead() {
        if (this.readTimer)
            this.win.clearTimeout(this.readTimer);
        this.readTimer = this.win.setTimeout(() => {
            const last = [...this.state.messages].reverse().find(message => !message.local && !message.status && message.index >= 0);
            if (last)
                void this.api.read(last.index).catch(() => undefined);
        }, 800);
        if (this.state.unread)
            this.set({ unread: 0 });
    }

    // ---------- actions ----------

    private actions: SiteChatActions = {
        toggle: () => {
            if (this.state.status === 'loading') {
                this.openRequested = !this.openRequested;
                return;
            }
            if (this.state.open)
                return this.actions.close();
            const hasThread = this.isSchool || this.state.config.skipHome || this.state.messages.length > 0 || this.state.localMessages.length > 0;
            // The thread opens at its newest message.
            this.threadAtBottom = true;
            this.set({ open: true, hint: null, view: hasThread ? 'thread' : 'home' });
            if (this.state.status === 'ready')
                void this.connectHub();
            if (hasThread)
                this.scheduleRead();
        },
        close: () => this.set({ open: false }),
        openThread: () => {
            this.threadAtBottom = true;
            this.set({ view: 'thread' });
            this.scheduleRead();
        },
        setAtBottom: (atBottom: boolean) => {
            if (atBottom === this.threadAtBottom)
                return;
            this.threadAtBottom = atBottom;
            if (atBottom && this.state.unread && this.seesNewest())
                this.scheduleRead();
        },
        back: () => this.set({ view: this.isSchool || this.state.config.skipHome ? 'thread' : 'home' }),
        setFormValue: (index, value) => {
            const formErrors = { ...this.state.formErrors };
            delete formErrors[index];
            this.set({ formValues: { ...this.state.formValues, [index]: value }, formErrors, formState: this.state.formState === 'network' ? 'idle' : this.state.formState });
        },
        submitForm: () => void this.submitForm(),
        skipForm: () => {
            if (this.formStatus().required)
                return;
            this.write({ leadSkipped: true });
            this.set({ leadSkipped: true, formErrors: {}, formState: 'idle' });
        },
        setText: text => {
            this.set({ text, composerError: null });
            this.sendTyping();
        },
        send: () => void this.send(),
        resend: message => void this.resend(message),
        attach: files => files.forEach(file => void this.upload(file)),
        removeUpload: key => {
            const upload = this.state.uploads.find(item => item.key === key);
            if (upload?.previewUrl)
                URL.revokeObjectURL(upload.previewUrl);
            this.uploadFiles.delete(key);
            this.set({ uploads: this.state.uploads.filter(item => item.key !== key) });
        },
        retryUpload: key => void this.sendUpload(key),
        retry: () => void this.start(),
        openHint: () => {
            const text = this.state.hint;
            this.dismissTrigger(this.state.hintKey);
            this.set({
                hint: null,
                open: true,
                view: 'thread',
                localMessages: text ? [{ id: `hint-${Date.now()}`, index: -1, text, fromVisitor: false, createdDate: this.now().toISOString(), local: true }] : [],
            });
            void this.connectHub();
        },
        dismissHint: () => {
            this.dismissTrigger(this.state.hintKey);
            this.set({ hint: null, hintKey: null });
        },
        clickButton: (message, button) => void this.clickButton(message, button),
        rate: (message, score, comment) => void this.rate(message, score, comment),
        loadMore: () => void this.loadMore(),
        sendVoice: voice => void this.sendVoice(voice),
        screenshot: () => void this.screenshot(),
    };

    private async screenshot() {
        try {
            const file = await captureScreen(this.win, () => {
                this.set({ capturing: true });
                return () => this.set({ capturing: false });
            });
            if (file)
                await this.upload(file);
        } catch {
            this.set({ composerError: siteChatUi(this.state.config.lang).screenshotFailed });
        }
    }

    private dismissTrigger(key: string | null) {
        if (!key)
            return;
        const stored = this.read();
        const dismissed = new Set(stored.dismissedTriggers ?? []);
        dismissed.add(key);
        this.write({ dismissedTriggers: [...dismissed].slice(-50) });
    }

    private async submitForm() {
        const { config, formValues } = this.state;
        const errors: Record<string, string> = {};
        config.fields.forEach((field, index) => {
            if (!field.enabled)
                return;
            const value = (formValues[index] ?? '').trim();
            if (!value) {
                if (field.required)
                    errors[index] = 'empty';
                return;
            }
            if (field.type === 'email' && !EMAIL_RE.test(value))
                errors[index] = 'email';
            // libphonenumber's verdict once the phone utils are in; before that, the old digit count.
            if (field.type === 'phone' && !(isValidPhone(value) ?? value.replace(/\D/g, '').length >= 7))
                errors[index] = 'phone';
            if (field.kind === 'number' && !Number.isFinite(Number(value.replace(/\s/g, '').replace(',', '.'))))
                errors[index] = 'invalid';
        });
        if (Object.keys(errors).length) {
            this.set({ formErrors: errors, formState: 'idle' });
            return;
        }

        this.set({ formState: 'sending', formErrors: {} });
        try {
            const values: Record<string, string> = {};
            config.fields.forEach((field, index) => {
                if (field.enabled && formValues[index]?.trim())
                    values[index] = formValues[index].trim();
            });
            const result = await this.api.lead({
                values,
                url: this.win.location.href,
                device: await readDevice(this.win),
                data: Object.keys(this.siteData).length ? { ...this.siteData } : undefined,
                contact: Object.keys(this.contactFields).length ? { ...this.contactFields } : undefined,
            });
            if (!result.leadSubmitted) {
                this.set({ formState: 'idle', formErrors: result.errors ?? {} });
                return;
            }
            this.set({ formState: 'idle', leadSubmitted: true, formForced: false });
            this.emit('formSubmitted', builtinValues(config.fields, values));
            void this.connectHub();
            await this.releaseHeld();
        } catch {
            this.set({ formState: 'network' });
        }
    }

    private sendTyping() {
        const now = Date.now();
        if (now - this.lastTypingSent < 3000 || this.hub?.state !== HubConnectionState.Connected || this.state.messages.length === 0)
            return;
        this.lastTypingSent = now;
        void this.hub.invoke('Typing').catch(() => undefined);
    }

    private async send() {
        const text = this.state.text.trim();
        const ready = this.state.uploads.filter(upload => upload.state === 'ready' && upload.attachment);
        if (!text && ready.length === 0)
            return;
        const form = this.formStatus();
        // The required form is already under a waiting message: nothing new until it is filled in.
        if (form.required && form.shown)
            return;

        const clientId = randomId();
        const optimistic: SiteChatMessage = {
            id: `local-${clientId}`,
            index: Number.MAX_SAFE_INTEGER,
            clientId,
            text,
            fromVisitor: true,
            createdDate: this.now().toISOString(),
            attachments: ready.map(upload => upload.attachment!),
            // The first message of a visitor the form is required from waits in the thread, under the form.
            status: form.required ? 'held' : 'sending',
        };
        this.set({
            text: '',
            uploads: this.state.uploads.filter(upload => {
                if (!ready.includes(upload))
                    return true;
                // Sent: the bubble shows the stored file from now on.
                if (upload.previewUrl)
                    URL.revokeObjectURL(upload.previewUrl);
                return false;
            }),
            messages: [...this.state.messages, optimistic],
            composerError: null,
            hint: null,
        });
        this.dismissTrigger(this.state.hintKey);
        this.emit('messageSent', { text, files: ready.length, voice: false });
        if (optimistic.status === 'held') {
            this.saveHeld();
            return;
        }
        await this.deliver(optimistic);
    }

    /**
     * A recorded voice message: shown at once from the local recording, uploaded as a voice file (the waveform goes
     * along as the in-app chat's AudioMeta, so the operator's player draws it too), then sent as a Voice message.
     */
    private async sendVoice(voice: SiteChatVoice) {
        const form = this.formStatus();
        if (form.required && form.shown)
            return;
        const clientId = randomId();
        const extension = voice.mimeType.includes('webm') ? 'webm' : voice.mimeType.includes('ogg') ? 'ogg' : 'mp4';
        // `audio/webm;codecs=opus` → `audio/webm`: the codec parameter is noise for storage and players.
        const mimeType = voice.mimeType.split(';')[0].trim() || `audio/${extension}`;
        const file = new File([voice.blob], `voice.${extension}`, { type: mimeType });
        const local: SiteChatAttachment = {
            id: `voice-${clientId}`,
            name: 'voice',
            extension: `.${extension}`,
            mimeType: file.type,
            size: file.size,
            type: 'audio',
            url: URL.createObjectURL(voice.blob),
            voice: true,
            duration: voice.duration,
            levels: voice.levels,
        };
        const optimistic: SiteChatMessage = {
            id: `local-${clientId}`,
            index: Number.MAX_SAFE_INTEGER,
            clientId,
            fromVisitor: true,
            createdDate: this.now().toISOString(),
            attachments: [local],
            status: form.required ? 'held' : 'sending',
        };
        this.voiceFiles.set(clientId, file);
        this.set({ messages: [...this.state.messages, optimistic], composerError: null, hint: null });
        this.emit('messageSent', { text: '', files: 0, voice: true });
        if (optimistic.status !== 'held')
            await this.deliver(optimistic);
    }

    private async resend(message: SiteChatMessage) {
        this.set({ messages: this.state.messages.map(item => item.id === message.id ? { ...item, status: 'sending' } : item) });
        await this.deliver(message);
    }

    private async deliver(message: SiteChatMessage) {
        try {
            let attachments = message.attachments ?? [];
            const voiceFile = message.clientId ? this.voiceFiles.get(message.clientId) : undefined;
            const voice = attachments[0]?.voice === true;
            if (voiceFile && voice && attachments[0].id.startsWith('voice-')) {
                const recorded = attachments[0];
                const uploaded = await this.api.upload(voiceFile, () => undefined, {
                    meta: JSON.stringify({
                        durationInSec: recorded.duration ?? 0,
                        frequencyLevels: recorded.levels ?? [],
                        codecName: voiceFile.type.includes('webm') ? 'opus' : undefined,
                        formatName: voiceFile.type,
                    }),
                });
                attachments = [{ ...uploaded, voice: true, duration: recorded.duration, levels: recorded.levels, url: recorded.url }];
                this.set({ messages: this.state.messages.map(item => item.id === message.id ? { ...item, attachments } : item) });
                message = { ...message, attachments };
            }
            const saved = await this.api.send({
                clientId: message.clientId!,
                text: message.text ?? '',
                fileIds: attachments.map(file => file.id),
                voice,
                device: await readDevice(this.win),
                url: this.win.location.href,
                data: Object.keys(this.siteData).length ? { ...this.siteData } : undefined,
                contact: Object.keys(this.contactFields).length ? { ...this.contactFields } : undefined,
            });
            if (message.clientId)
                this.voiceFiles.delete(message.clientId);
            this.set({ messages: mergeMessages(this.state.messages.filter(item => item.id !== message.id), [saved]) });
            void this.connectHub();
            this.scheduleRead();
        } catch (e) {
            const code = e instanceof SiteChatError ? e.code : 'network';
            if (code === 'lead_required') {
                // Hours or settings changed under the visitor: the message waits under the form, now required.
                this.set({
                    messages: this.state.messages.map(item => item.id === message.id ? { ...item, status: 'held' } : item),
                    formForced: true,
                });
                this.saveHeld();
                return;
            }
            this.set({
                messages: this.state.messages.map(item => item.id === message.id ? { ...item, status: 'failed' } : item),
                composerError: code === 'rate_limited' ? siteChatUi(this.state.config.lang).rateLimited
                    : code === 'voice_disabled' ? siteChatUi(this.state.config.lang).voiceFailed : null,
            });
        }
    }

    private async upload(file: File) {
        const key = randomId();
        const extension = file.name.includes('.') ? (file.name.split('.').pop() ?? '').toLowerCase() : '';
        const kind = fileKind(file.type, extension);
        const allowed = this.state.config.allowedExtensions;
        const maxSize = Math.min(this.maxFileSize, this.state.config.maxFileSizeMb * 1024 * 1024);
        const state: SiteChatUpload['state'] = file.size > maxSize
            ? 'too-large'
            : !extension || FORBIDDEN_EXTENSIONS.includes(extension) || (allowed.length > 0 && !allowed.includes(extension)) ? 'type' : 'uploading';
        const previewUrl = state === 'uploading' && (kind === 'picture' || kind === 'video') ? URL.createObjectURL(file) : null;
        this.set({ uploads: [...this.state.uploads, { key, name: file.name, extension, kind, size: file.size, state, progress: 0, previewUrl }] });
        if (state !== 'uploading')
            return;
        this.uploadFiles.set(key, file);
        await this.sendUpload(key);
    }

    /** Uploads (or re-uploads after a failure) the file kept under this chip. */
    private async sendUpload(key: string) {
        const file = this.uploadFiles.get(key);
        if (!file)
            return;
        const update = (patch: Partial<SiteChatUpload>) =>
            this.set({ uploads: this.state.uploads.map(upload => upload.key === key ? { ...upload, ...patch } : upload) });
        update({ state: 'uploading', progress: 0 });
        try {
            const attachment = await this.api.upload(file, progress => update({ progress }));
            this.uploadFiles.delete(key);
            update({ state: 'ready', progress: 100, attachment });
        } catch (e) {
            const code = e instanceof SiteChatError ? e.code : '';
            update({ state: code === 'file_too_large' ? 'too-large' : code === 'file_type' ? 'type' : 'error' });
        }
    }

    private async clickButton(message: SiteChatMessage, button: SiteChatButton) {
        try {
            await this.api.button(message.id, button.index);
        } catch {
            // A click that did not reach the scenario is not worth an error screen; the visitor can click again.
        }
    }

    /** The latest answer sent per «rate the conversation» card: an older request's late reply is not drawn over it. */
    private csatRequests = new Map<string, number>();

    /**
     * «Rate the conversation»: the card shows the answer at once and takes the server's copy when it comes. Refused as
     * closed (the conversation reopened, someone wrote, a week passed) — the card freezes on what the server has; not
     * delivered — it goes back to the answer from before and says so.
     */
    private async rate(message: SiteChatMessage, score: number, comment?: string | null) {
        const card = this.state.messages.find(item => item.id === message.id);
        if (!card || !isCsatScore(score) || !csatAnswerable(this.state.messages, card, this.now().getTime()))
            return;
        // the same face again, nothing new to say
        if (card.csat?.score === score && comment == null)
            return;
        const before = card.csat ?? null;
        const request = (this.csatRequests.get(card.id) ?? 0) + 1;
        this.csatRequests.set(card.id, request);
        const patch = (change: (item: SiteChatMessage) => SiteChatMessage) =>
            this.set({ messages: this.state.messages.map(item => item.id === card.id ? change(item) : item) });
        patch(item => ({ ...withCsatAnswer(item, score, comment), csatStatus: 'sending' }));
        try {
            const saved = await this.api.csat(card.id, score, comment);
            if (this.csatRequests.get(card.id) !== request)
                return;
            if (saved?.id === card.id)
                this.set({ messages: mergeMessages(this.state.messages, [saved]) });
            else
                patch(item => ({ ...item, csatStatus: undefined }));
        } catch (e) {
            if (this.csatRequests.get(card.id) !== request)
                return;
            const code = e instanceof SiteChatError ? e.code : 'network';
            const closed = code === 'csat_closed' || code === 'not_found';
            patch(item => ({ ...item, csat: before, csatStatus: closed ? 'closed' : 'failed' }));
        }
    }

    /** The list asks again every time the reader reaches the top; one page is fetched at a time. */
    private loadingMore = false;

    private async loadMore() {
        const first = this.state.messages.find(message => !message.local && message.index >= 0);
        if (!first || this.loadingMore || !this.state.hasMore)
            return;
        this.loadingMore = true;
        try {
            const page = await this.api.messages(first.index);
            this.set({ messages: mergeMessages(this.state.messages, page.messages), hasMore: page.hasMore });
        } catch {
            // Keep the button; the next click retries.
        } finally {
            this.loadingMore = false;
        }
    }

    // ---------- storage ----------

    /** Files of the composer's chips until they are uploaded, by chip key: «retry» sends the same file again. */
    private uploadFiles = new Map<string, File>();

    /** Recordings still waiting to be uploaded, by client id: a failed send retries with the same file. */
    private voiceFiles = new Map<string, File>();

    private get storageKey() {
        return `axl-site-chat:${this.options.channel}`;
    }

    private read(): Stored {
        try {
            return JSON.parse(this.win.localStorage.getItem(this.storageKey) ?? '{}') ?? {};
        } catch {
            return {};
        }
    }

    private write(patch: Stored) {
        if (this.isSchool)
            return;
        try {
            this.win.localStorage.setItem(this.storageKey, JSON.stringify({ ...this.read(), ...patch }));
        } catch {
            // Private mode or blocked storage: the visitor gets a new id on the next visit, nothing breaks.
        }
    }
}

/**
 * The visitor's messages up to the returned index count as read: the operators' own mark, or the latest message from the
 * school's side (an operator, the AI assistant, a scenario) — whoever answered has seen what came before.
 */
export function readIndexOf(messages: SiteChatMessage[], operatorsRead: number) {
    let index = operatorsRead;
    for (const message of messages)
        // A hand-over note is the system speaking, not somebody who read the thread.
        if (!message.fromVisitor && !message.local && !message.status && !message.notice && message.index > index)
            index = message.index;
    return index;
}

/** Upserts by id, replaces a pending bubble by its client id and keeps the thread in server order. */
/** A draft with no news for this long is dropped: its reply is not coming (the server withdraws it in every other case). */
const DRAFT_TIMEOUT_MS = 30_000;

type DraftEvent = { id?: string; text?: string | null; markdown?: boolean; withdrawn?: boolean; author?: SiteChatAuthor | null };
type DraftState = { id: string; text: string; markdown: boolean; author: SiteChatAuthor | null; createdDate: string };

/**
 * The draft as the thread's last bubble: a client-side message keyed by the draft id, which the stored reply carries as
 * its `clientId` — when it arrives (live or by a catch-up page) the draft row is gone and the reply sits in its place.
 */
export function draftRow(draft: DraftState | null, messages: SiteChatMessage[]): SiteChatMessage[] {
    if (!draft || messages.some(message => message.clientId === draft.id))
        return [];
    const last = messages[messages.length - 1];
    return [{
        id: `draft:${draft.id}`,
        clientId: draft.id,
        index: (last?.index ?? 0) + 0.5,
        text: draft.text,
        markdown: draft.markdown,
        fromVisitor: false,
        createdDate: draft.createdDate,
        author: draft.author,
        draft: true,
    }];
}

export function mergeMessages(current: SiteChatMessage[], incoming: SiteChatMessage[]): SiteChatMessage[] {
    const byId = new Map(current.map(message => [message.id, message]));
    for (const message of incoming) {
        if (message.clientId) {
            for (const [id, item] of byId) {
                if (item.clientId === message.clientId && id !== message.id)
                    byId.delete(id);
            }
        }
        // A «rate the conversation» card the server refused stays frozen when its copy comes again (another tab, a page).
        const closed = byId.get(message.id)?.csatStatus === 'closed';
        byId.set(message.id, closed ? { ...message, status: undefined, csatStatus: 'closed' } : { ...message, status: undefined });
    }
    return [...byId.values()].sort((a, b) => a.index - b.index);
}

function randomId() {
    const bytes = new Uint8Array(16);
    (globalThis.crypto ?? window.crypto).getRandomValues(bytes);
    return Array.from(bytes, byte => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'[byte & 63]).join('').slice(0, 22);
}

export function fileKind(mimeType: string | null | undefined, extension: string): import('../types').SiteChatFileKind {
    const mime = (mimeType ?? '').toLowerCase();
    if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic'].includes(extension))
        return 'picture';
    if (mime.startsWith('video/') || ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv'].includes(extension))
        return 'video';
    if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'opus'].includes(extension))
        return 'audio';
    return 'document';
}

/** The page parameter the visitor email's «Continue chat» link carries (server: `SiteChatOfflineEmail.ResumeParameter`). */
export const RESUME_PARAMETER = 'axl_chat';

/**
 * The resume token of the email's link, taken out of the address bar at once so a copied or shared link does not hand
 * the conversation on. Null when the page was not opened from such a link.
 */
export function takeResumeToken(win: Window): string | null {
    try {
        const url = new URL(win.location.href);
        const token = url.searchParams.get(RESUME_PARAMETER);
        if (token === null)
            return null;
        url.searchParams.delete(RESUME_PARAMETER);
        win.history?.replaceState?.(win.history.state, '', url.toString());
        return /^[a-z2-7]{24}$/.test(token.trim().toLowerCase()) ? token.trim().toLowerCase() : null;
    } catch {
        return null;
    }
}

export function isPhone(win: Window) {
    return win.matchMedia?.('(max-width: 759px)').matches ?? false;
}

/** The lead form's built-in field a `setVisitor` key fills. */
function builtinKey(type: SiteChatConfig['fields'][number]['type']): keyof SiteChatVisitor {
    return type === 'email' ? 'email' : type === 'phone' ? 'phone' : 'name';
}

/**
 * The `setVisitor` value for one form field: a built-in one by `name` / `email` / `phone` (the name also by `firstName`),
 * one bound to a contact field by that field's key (`attr`: `city`, `birthday`, a custom field's id).
 */
function visitorValueFor(field: SiteChatConfig['fields'][number], visitor: SiteChatVisitor) {
    // A custom contact field answers to its id and to its key (`attrKey`, stamped by the admin on save).
    const bound = field.attr ? visitor[field.attr] ?? (field.attrKey ? visitor[field.attrKey] : undefined) : undefined;
    if (!field.builtin)
        return bound;
    const key = builtinKey(field.type);
    return visitor[key] ?? bound ?? (key === 'name' ? visitor.firstName : undefined);
}

/** The built-in fields of a submitted form (values by field index), for the page's `formSubmitted` listeners. */
function builtinValues(fields: SiteChatConfig['fields'], values: Record<string, string>): SiteChatVisitor {
    const lead: SiteChatVisitor = {};
    fields.forEach((field, index) => {
        if (field.builtin && values[index])
            lead[builtinKey(field.type)] = values[index];
    });
    return lead;
}

function browserLang(win: Window): SiteChatLang {
    const tag = (win.navigator.language || 'en').toLowerCase();
    if (tag.startsWith('zh'))
        return tag.includes('tw') || tag.includes('hk') || tag.includes('hant') ? 'zhtw' : 'zhcn';
    const short = tag.slice(0, 2) as SiteChatLang;
    return SITE_CHAT_LANGS.includes(short) ? short : 'en';
}
